import React, { useState, useCallback, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Input, Button, Skeleton, Empty, Alert, Pagination } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import BannerBar from "../../../components/Bannerbar";
import RenderHelmet from "../../../components/RenderHelmet";
import { buscar, getBreadcrumb } from "../../../services/constituicaoAPI";
import style from "./style.module.css";

const { Search } = Input;

/** URL truncada até o artigo (remove inciso, parágrafo, etc.) */
function urlAteArtigo(url) {
  if (!url || typeof url !== "string") return url;
  const m = url.match(/^(.+?-art-\d+)/);
  return m ? m[1] : url;
}

/** Formata breadcrumb para exibição: "Título II Capítulo I Art. 5º" */
function formatarBreadcrumbLabel(bc) {
  if (!bc?.data?.length) return null;
  return bc.data
    .map((b) => (b.titulo || "").split(":")[0].trim())
    .filter(Boolean)
    .join(" ");
}

function ResultadoItem({ item, query }) {
  const getLink = () => {
    if (item.url) return `/legislacao/federal/${urlAteArtigo(item.url)}`;
    return `/legislacao/federal/elemento/${item.id}`;
  };

  const highlightTrecho = (trecho) => {
    if (!trecho || !query) return trecho;
    const q = (query || "").trim();
    if (!q) return trecho;
    const idx = trecho.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return trecho;
    const before = trecho.substring(0, idx);
    const match = trecho.substring(idx, idx + q.length);
    const after = trecho.substring(idx + q.length);
    return (
      <>
        {before}
        <mark className={style.trechoHighlight}>{match}</mark>
        {after}
      </>
    );
  };

  const tituloCompleto = item.breadcrumbLabel || item.titulo;

  return (
    <div className={style.resultadoItem}>
      <Link to={getLink()} className={style.resultadoTitulo}>
        {tituloCompleto}
      </Link>
      {item.trecho && (
        <p className={style.resultadoTrecho}>…{highlightTrecho(item.trecho)}…</p>
      )}
    </div>
  );
}

export default function ConstituicaoPesquisa() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const qFromUrl = params.get("q") || "";
  const pageFromUrl = parseInt(params.get("page") || "1", 10);

  const [query, setQuery] = useState(qFromUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);

  const execPesquisa = useCallback(
    async (termo = query, page = 1) => {
      const q = (termo || query).trim();
      if (!q) {
        setError("Digite um termo para pesquisar.");
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const data = await buscar(q, { page, size: 20 });
        const itens = data?.data || [];
        if (itens.length > 0) {
          const breadcrumbs = await Promise.allSettled(
            itens.map((item) => getBreadcrumb(item.id))
          );
          const enriquecidos = itens.map((item, i) => {
            const bc = breadcrumbs[i]?.status === "fulfilled" ? breadcrumbs[i].value : null;
            const label = formatarBreadcrumbLabel(bc);
            return { ...item, breadcrumbLabel: label || item.titulo };
          });
          setResultado({ ...data, data: enriquecidos });
        } else {
          setResultado(data);
        }
        setQuery(q);
        const newSearch = new URLSearchParams();
        newSearch.set("q", q);
        newSearch.set("page", String(page));
        history.replace({ search: newSearch.toString() });
      } catch (err) {
        setError(err?.message || "Erro ao pesquisar.");
        setResultado(null);
      } finally {
        setLoading(false);
      }
    },
    [query, history]
  );

  const onSearch = (value) => execPesquisa(value || query, 1);
  const onPageChange = (page) => execPesquisa(query, page);

  useEffect(() => {
    if (!qFromUrl.trim()) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await buscar(qFromUrl, { page: pageFromUrl, size: 20 });
        const itens = data?.data || [];
        const enriquecidos = await Promise.all(
          itens.map(async (item) => {
            try {
              const bc = await getBreadcrumb(item.id);
              const label = formatarBreadcrumbLabel(bc);
              return { ...item, breadcrumbLabel: label || item.titulo };
            } catch {
              return { ...item, breadcrumbLabel: item.titulo };
            }
          })
        );
        if (!cancelled) {
          setResultado({ ...data, data: enriquecidos });
          setQuery(qFromUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Erro ao pesquisar.");
          setResultado(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [qFromUrl, pageFromUrl]);

  const data = resultado?.data || [];
  const total = resultado?.total ?? 0;
  const curPage = resultado?.curPage ?? 1;

  return (
    <>
      <BannerBar customText="/constituição - pesquisar" />
      <div className="container">
        <RenderHelmet
          title="Pesquisar na Constituição Federal - Cadê Direito"
          description="Busca textual na Constituição da República Federativa do Brasil de 1988."
        />

        <div className={style.buscaMinimalista}>
          <div className={style.buscaCampoWrap}>
            <Search
              placeholder="Pesquisar na Constituição Federal..."
              enterButton={
                <Button type="primary" icon={<SearchOutlined />} size="large">
                  Pesquisar
                </Button>
              }
              size="large"
              onSearch={onSearch}
              onChange={(e) => setQuery(e.target.value)}
              value={query}
              loading={loading}
              autoFocus
              className={style.buscaInput}
            />
          </div>

          <Link to="/legislacao/federal" className={style.linkVoltar}>
            ← Voltar ao sumário
          </Link>

          {error && (
            <Alert
              type="warning"
              message={error}
              showIcon
              closable
              onClose={() => setError(null)}
              className={style.alertaErro}
            />
          )}

          {loading && (
            <div className={style.loadingWrap}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          )}

          {!loading && resultado && (
            <div className={style.resultadosWrap}>
              {total > 0 && (
                <p className={style.resultadosInfo}>
                  <strong>{total}</strong> resultado{total !== 1 ? "s" : ""} para &quot;{resultado.query}&quot;
                </p>
              )}

              {data.length === 0 ? (
                <Empty
                  description={
                    resultado.query
                      ? "Nenhum resultado encontrado. Tente outro termo."
                      : "Digite um termo e clique em Pesquisar."
                  }
                  className={style.emptyState}
                />
              ) : (
                <>
                  <ul className={style.listaResultados}>
                    {data.map((item) => (
                      <li key={item.id}>
                        <ResultadoItem item={item} query={resultado.query} />
                      </li>
                    ))}
                  </ul>
                  {total > 20 && (
                    <div className={style.paginacaoWrap}>
                      <Pagination
                        current={curPage}
                        total={total}
                        pageSize={20}
                        onChange={onPageChange}
                        showSizeChanger={false}
                        simple
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
